
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import AppFooter from "../AppFooter";
import { FaTimes, FaPaperPlane } from "react-icons/fa";

const CollaborationInvitation = () => {
  const { tenant } = useParams();
  const nav = useNavigate();
  const { state } = useLocation(); // optional: prefilled event/item
  const baseUrl =
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");

  const [query, setQuery] = useState("");
  const [tenants, setTenants] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [scope, setScope] = useState(state?.scope || "event"); // "event" | "item"
  const [eventId, setEventId] = useState(state?.eventId || "");
  const [itemId, setItemId] = useState(state?.itemId || "");
  const [message, setMessage] = useState(state?.message || "");
  const [visibleDirectory, setVisibleDirectory] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    // load my visibility setting
    axios
      .get(`${baseUrl}/api/collab/${tenant}/settings`)
      .then((r) => setVisibleDirectory(!!r.data?.showInCollabDirectory))
      .catch(() => {});
  }, [tenant, baseUrl]);

  useEffect(() => {
    if (!query?.trim()) {
      setTenants([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${baseUrl}/api/collab/${tenant}/searchTenants`, {
          params: { query },
        });
        setTenants(r.data?.tenants || []);
      } catch (e) {
        console.error(e);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, tenant, baseUrl]);

  const canSend = recipient && ((scope === "event" && eventId) || (scope === "item" && eventId && itemId));

  const sendInvite = async () => {
    try {
      if (!canSend) return;
      setSending(true);
      const url =
        scope === "event"
          ? `${baseUrl}/api/collab/${tenant}/events/${eventId}/share`
          : `${baseUrl}/api/collab/${tenant}/events/${eventId}/items/${itemId}/share`;
      const r = await axios.post(url, {
        toTenantId: recipient.id,
        message,
      });
      if (r.data?.success) {
        alert("Invitation sent.");
        nav(-1);
      } else {
        alert(r.data?.message || "Failed to send invitation.");
      }
    } catch (e) {
      console.error(e);
      alert("Failed to send invitation.");
    } finally {
      setSending(false);
    }
  };

  const saveVisibility = async (next) => {
    try {
      setVisibleDirectory(next);
      await axios.patch(`${baseUrl}/api/collab/${tenant}/settings`, {
        showInCollabDirectory: next,
      });
    } catch (e) {
      console.error(e);
      // non-fatal
    }
  };

  return (
    <div className="container">
      <h2 className="hdr hdr--primary hdr--lg">Collaboration Invitation</h2>

      <div className="mb-4">
        <label className="block mb-1 font-semibold">My Visibility in Collaboration Directory</label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={visibleDirectory}
            onChange={(e) => saveVisibility(e.target.checked)}
          />
          <span>Show my tenant to others</span>
        </label>
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-semibold">Invite Scope</label>
        <select className="form-control" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="event">Event</option>
          <option value="item">Event Item</option>
        </select>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block mb-1">Event ID</label>
          <input className="form-control" value={eventId} onChange={(e) => setEventId(e.target.value)} />
        </div>
        {scope === "item" && (
          <div>
            <label className="block mb-1">Item ID</label>
            <input className="form-control" value={itemId} onChange={(e) => setItemId(e.target.value)} />
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-semibold">Search Tenant</label>
        <input
          className="form-control"
          placeholder="Name, email, or handle…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {!!tenants.length && (
          <div className="rounded border mt-2 divide-y">
            {tenants.map((t) => (
              <button
                key={t.id}
                className={`w-full text-left p-2 ${recipient?.id === t.id ? "bg-gray-800 text-white" : ""}`}
                onClick={() => setRecipient(t)}
                type="button"
              >
                <div className="font-semibold">{t.name}</div>
                <div className="opacity-70 text-sm">#{t.id}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-semibold">Message (optional)</label>
        <textarea className="form-control" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>

      <AppFooter
        buttons={[
          { icon: <FaTimes />, label: "Cancel", onClick: () => nav(-1) },
          { icon: <FaPaperPlane />, label: sending ? "Sending…" : "Send Invite", onClick: sendInvite, disabled: !canSend || sending },
        ]}
      />
    </div>
  );
};

export default CollaborationInvitation;
